import type { Project, Equipment, AMIDataPoint } from '../types/project';
import { determineSeason } from '../lib/calculations';
import { isConcurrencyOverridden, hasOwnConcurrencyReason } from '../lib/concurrency';
import { existingLoads, proposedLoads, removedLoads, isBlankProposedRow } from '../lib/calc/loadRoles';
import { checkTraditionalEligibility } from '../lib/calc/eligibility';
import { effectiveReportMethod } from '../lib/calc/reportBasis';
import { citationsFor } from '../lib/calc/citations';
import { traditionalQualityIssues } from '../lib/profileQuality';

export type ValidationSeverity = 'BLOCKER' | 'WARNING' | 'ADVISORY';

export interface ValidationItem {
  id: string;
  severity: ValidationSeverity;
  title: string;
  message: string;
  fieldPath?: string;
  section?: string;
  fixAction?: { type: 'NAVIGATE'; target: string } | { type: 'NONE' };
}

export interface ValidationResult {
  blockers: ValidationItem[];
  warnings: ValidationItem[];
  advisories: ValidationItem[];
  summary: {
    blockerCount: number;
    warningCount: number;
    advisoryCount: number;
  };
}

export interface ValidationSnapshot {
  completedAt: string;
  blockerCount: number;
  warningCount: number;
  advisoryCount: number;
  warningAcknowledged: boolean;
}

// ── Snapshot persistence (localStorage, separate from project data) ──────────

export function saveValidationSnapshot(projectId: string, snapshot: ValidationSnapshot): void {
  try {
    localStorage.setItem(`trueload_validation_${projectId}`, JSON.stringify(snapshot));
  } catch {
    // localStorage may be unavailable in some environments
  }
}

export function getValidationSnapshot(projectId: string): ValidationSnapshot | null {
  try {
    const raw = localStorage.getItem(`trueload_validation_${projectId}`);
    return raw ? (JSON.parse(raw) as ValidationSnapshot) : null;
  } catch {
    return null;
  }
}

// ── Field → tab navigation map ───────────────────────────────────────────────

/**
 * Maps a validation fieldPath to the tab ID that contains the field.
 *
 * Only paths this file actually emits belong here — an entry for a field no rule
 * references is dead weight that reads like a rule exists when none does.
 */
export const FIELD_TO_TAB: Record<string, string> = {
  customer_name: 'project',
  customer_address: 'project',
  customer_zip: 'project',
  ahj_jurisdiction: 'project',
  prepared_by: 'project',
  preparer_email: 'project',
  preparer_license: 'project',
  assumptions_notes: 'project',
  service_voltage_numeric: 'service',
  service_rating_amps: 'service',
  utility_id: 'service',
  equipment: 'equipment',
  ami_data: 'ami',
  ami_data_quality_notes: 'ami',
  dwelling_floor_area_sqft: 'ami',
  inventory_attested: 'ami',
  existing_loads: 'ami',
  occupancy_type: 'ami',
  // The demand-parameters panel lives on the Results tab.
  demand_parameters: 'results',
};

function nav(fieldPath: string): { type: 'NAVIGATE'; target: string } {
  return { type: 'NAVIGATE', target: FIELD_TO_TAB[fieldPath] ?? 'project' };
}

/**
 * The one definition of a usable email address.
 *
 * Exported so the inline message under the input and the completion gate cannot
 * disagree: the form used to hold its own copy that only ever produced a hint,
 * so a malformed preparer address passed completion untouched.
 */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Peak season, derived the same way the calculation derives it: the timestamp of
 * the highest reading, falling back to the stored peak. Returns 'Unknown' when
 * there is nothing to go on — in which case concurrency cannot be auto-determined,
 * the checkbox is disabled in the UI, and the override rule below has nothing to
 * compare against.
 */
function computePeakSeason(project: Partial<Project>, amiData: AMIDataPoint[]): string {
  if (amiData.length > 0) {
    let peak = amiData[0];
    for (const p of amiData) if (p.kw > peak.kw) peak = p;
    return determineSeason(peak.timestamp);
  }
  return determineSeason(project.peak_timestamp ?? null);
}

function computeDateRangeDays(amiData: AMIDataPoint[]): number | null {
  if (amiData.length < 2) return null;
  let minTs = Infinity;
  let maxTs = -Infinity;
  for (const p of amiData) {
    const t = new Date(p.timestamp).getTime();
    if (t < minTs) minTs = t;
    if (t > maxTs) maxTs = t;
  }
  if (!isFinite(minTs) || !isFinite(maxTs)) return null;
  return (maxTs - minTs) / (1000 * 60 * 60 * 24);
}

// ── Main validation function ─────────────────────────────────────────────────

/**
 * Validates a project for completion readiness under its report basis.
 *
 * The basis (`project.report_method`) selects which existing-load rules apply:
 * 'measured' (the default, including null) keeps the NEC 220.87 requirements
 * exactly as they have always been; 'traditional' swaps the AMI requirements
 * for the dwelling-profile ones (NEC 220.83/120.83). Identity, service,
 * utility, and proposed-equipment rules are shared.
 *
 * Accepts the current in-memory state so validation reflects unsaved edits.
 * Safe to call repeatedly — never mutates project state. Pass the FULL
 * equipment list — the validator splits proposed rows from dwelling-inventory
 * rows itself (a pre-filtered proposed list also works: the inventory rules
 * simply see an empty inventory).
 *
 * @param _projectId  Project ID (reserved for future async lookups; unused today)
 * @param project     Current project fields from React state
 * @param equipment   Current equipment list from React state
 * @param amiData     Current AMI data points from React state (loaded from IDB)
 */
export async function validateProjectForCompletion(
  _projectId: string,
  project: Partial<Project>,
  equipment: Equipment[],
  amiData: AMIDataPoint[]
): Promise<ValidationResult> {
  const blockers: ValidationItem[] = [];
  const warnings: ValidationItem[] = [];
  const advisories: ValidationItem[] = [];

  // The basis the report actually prints (reportBasis.ts) — never a stored
  // choice whose path cannot run while the other path can.
  const method = effectiveReportMethod(project, amiData);
  // Measured-path blockers name the section the project's code edition
  // actually uses — 220.87, or 120.87 under the 2026 Article 120 reorg.
  // Blockers that apply to both bases name no section at all: they were
  // citing 220.87 even on traditional projects.
  const cites = citationsFor(project);
  const cite = cites.measured;
  // The section the report basis actually cites — for warnings that speak
  // about "this evaluation" rather than about measured data.
  const basisCite =
    method === 'traditional' ? (cites.pack.sectionMap.existingDwelling ?? cite) : cite;
  const proposed = proposedLoads(equipment);
  const inventory = existingLoads(equipment);

  // ─────────────────────────────────────────────────────────────────────────
  // TIER 1 — HARD BLOCKERS
  // ─────────────────────────────────────────────────────────────────────────

  // Project identification ──────────────────────────────────────────────────

  if (!project.customer_name?.trim()) {
    blockers.push({
      id: 'customer_name_required',
      severity: 'BLOCKER',
      title: 'Customer / Site Name missing',
      message: 'The customer or site name is required on every report.',
      fieldPath: 'customer_name',
      section: 'Project Info',
      fixAction: nav('customer_name'),
    });
  }

  if (!project.customer_address?.trim()) {
    blockers.push({
      id: 'customer_address_required',
      severity: 'BLOCKER',
      title: 'Property Address missing',
      message: 'The property address is required to identify the service location in the report.',
      fieldPath: 'customer_address',
      section: 'Project Info',
      fixAction: nav('customer_address'),
    });
  }

  if (!project.prepared_by?.trim()) {
    blockers.push({
      id: 'prepared_by_required',
      severity: 'BLOCKER',
      title: 'Prepared By missing',
      message: "The preparer's name is required on every report.",
      fieldPath: 'prepared_by',
      section: 'Project Info',
      fixAction: nav('prepared_by'),
    });
  }

  const zip = project.customer_zip?.trim() ?? '';
  if (!zip) {
    blockers.push({
      id: 'customer_zip_required',
      severity: 'BLOCKER',
      title: 'ZIP Code missing',
      message: 'The property ZIP code is required to identify the service location.',
      fieldPath: 'customer_zip',
      section: 'Project Info',
      fixAction: nav('customer_zip'),
    });
  } else if (!/^\d{5}$/.test(zip)) {
    // The ZIP is what matches the property to the utilities that serve it, so a
    // malformed one silently drops the coverage match rather than erroring.
    blockers.push({
      id: 'customer_zip_invalid',
      severity: 'BLOCKER',
      title: 'ZIP Code is not a valid 5-digit code',
      message: `"${zip}" is not a 5-digit ZIP code. The ZIP identifies the service location and is used to match the property to the utilities that serve it.`,
      fieldPath: 'customer_zip',
      section: 'Project Info',
      fixAction: nav('customer_zip'),
    });
  }

  const preparerEmail = project.preparer_email?.trim() ?? '';
  if (!preparerEmail) {
    blockers.push({
      id: 'preparer_email_required',
      severity: 'BLOCKER',
      title: 'Preparer Email missing',
      message: "The preparer's email address is required for report contact information.",
      fieldPath: 'preparer_email',
      section: 'Project Info',
      fixAction: nav('preparer_email'),
    });
  } else if (!isValidEmail(preparerEmail)) {
    // This address is offered to the customer in the AMI authorization email as
    // the way to check the request is genuine. A blank one falls back to the
    // account address; a malformed one is used verbatim and simply bounces.
    blockers.push({
      id: 'preparer_email_invalid',
      severity: 'BLOCKER',
      title: 'Preparer Email is not a valid address',
      message: `"${preparerEmail}" is not a valid email address. Customers are asked to contact you at this address to verify AMI authorization requests, so it has to be one that works.`,
      fieldPath: 'preparer_email',
      section: 'Project Info',
      fixAction: nav('preparer_email'),
    });
  }

  // Service description ──────────────────────────────────────────────────────

  if (!project.service_rating_amps || project.service_rating_amps <= 0) {
    blockers.push({
      id: 'service_rating_required',
      severity: 'BLOCKER',
      title: 'Service Rating (Amps) missing',
      message:
        'The existing service ampere rating is required to compute service adequacy.',
      fieldPath: 'service_rating_amps',
      section: 'Service Info',
      fixAction: nav('service_rating_amps'),
    });
  }

  if (!project.service_voltage_numeric || project.service_voltage_numeric <= 0) {
    blockers.push({
      id: 'service_voltage_required',
      severity: 'BLOCKER',
      title: 'Service Voltage missing',
      message: 'Service voltage is required to calculate service capacity in kVA/kW.',
      fieldPath: 'service_voltage_numeric',
      section: 'Service Info',
      fixAction: nav('service_voltage_numeric'),
    });
  }

  if (!project.utility_id && !project.ami_data_provider?.trim()) {
    blockers.push({
      id: 'utility_provider_required',
      severity: 'BLOCKER',
      title: 'Utility Provider missing',
      message: 'A utility provider must be selected or entered before completing the project.',
      fieldPath: 'utility_id',
      section: 'Service Info',
      fixAction: nav('utility_id'),
    });
  }

  // Existing-load basis — method-scoped ─────────────────────────────────────
  //
  // Measured basis: the 220.87 data requirements, unchanged. Traditional
  // basis: the dwelling-profile requirements instead — a traditional project
  // is never blocked on AMI data, and a measured project is never blocked on
  // floor area or attestation.

  if (method === 'traditional') {
    // Scope first: a project outside the section's applicability cannot
    // complete on the traditional basis no matter how complete its inputs.
    const gate = checkTraditionalEligibility(project);
    if (!gate.eligible) {
      blockers.push({
        id: 'traditional_ineligible',
        severity: 'BLOCKER',
        title:
          gate.reason === 'occupancy-unknown'
            ? 'Occupancy not answered'
            : 'Traditional calculation does not apply to this project',
        message: `${gate.message} ${gate.alternative}`,
        fieldPath: 'occupancy_type',
        section: 'Existing Load',
        fixAction: nav('occupancy_type'),
      });
    }

    if (!project.dwelling_floor_area_sqft || project.dwelling_floor_area_sqft <= 0) {
      blockers.push({
        id: 'dwelling_floor_area_required',
        severity: 'BLOCKER',
        title: 'Dwelling floor area missing',
        message:
          'The traditional calculation derives general lighting load from floor area. Enter the floor area on the Existing Load tab (Traditional calculation).',
        fieldPath: 'dwelling_floor_area_sqft',
        section: 'Existing Load',
        fixAction: nav('dwelling_floor_area_sqft'),
      });
    }

    const unratedInventory = inventory.filter(eq => !eq.amps && !eq.kw);
    if (unratedInventory.length > 0) {
      const name = (eq: Equipment) => eq.equipment_name?.trim() || 'Unnamed item';
      blockers.push({
        id: 'existing_load_rating_required',
        severity: 'BLOCKER',
        title: `${unratedInventory.length} existing load(s) missing a rating`,
        message: `The following dwelling-inventory items have no kW or ampere rating and would understate the calculated load: ${unratedInventory.map(name).join(', ')}. Enter the nameplate rating or remove the item.`,
        fieldPath: 'existing_loads',
        section: 'Existing Load',
        fixAction: nav('existing_loads'),
      });
    }

    if (!project.inventory_attested) {
      blockers.push({
        id: 'inventory_attested_required',
        severity: 'BLOCKER',
        title: 'Appliance inventory not attested',
        message:
          'The traditional calculation is only as good as the inventory behind it. Confirm on the Existing Load tab that every fastened-in-place, permanently connected, or dedicated-circuit appliance is listed — the attestation prints on the report.',
        fieldPath: 'inventory_attested',
        section: 'Existing Load',
        fixAction: nav('inventory_attested'),
      });
    }

    const defaultsInUse = inventory.filter(eq => eq.value_source === 'default').length;
    if (defaultsInUse > 0) {
      warnings.push({
        id: 'dwelling_defaults_in_use',
        severity: 'WARNING',
        title: `${defaultsInUse} inventory value(s) are standard defaults`,
        message:
          'Standard values are starting points, not readings. Verify each against the equipment nameplate where accessible — the report discloses how many defaults were used.',
        fieldPath: 'existing_loads',
        section: 'Existing Load',
        fixAction: nav('existing_loads'),
      });
    }

    // Profile sanity checks (lib/profileQuality.ts): a short inventory, a
    // circuit count under the code minimum, an implausible floor area, a
    // dryer that cannot be on the circuit it is flagged for, and any
    // preparer-stated demand parameter. None changes a number; each has to be
    // acknowledged, because without interval data nothing else says "this
    // list is short".
    for (const issue of traditionalQualityIssues(project, equipment)) {
      warnings.push({
        id: issue.id,
        severity: 'WARNING',
        title: issue.title,
        message: issue.message,
        fieldPath: issue.fieldPath,
        section: issue.fieldPath === 'demand_parameters' ? 'Results' : 'Existing Load',
        fixAction: nav(issue.fieldPath),
      });
    }
  }

  // Measured demand adequacy (NEC 220.87) ────────────────────────────────────

  const hasPeakDemand =
    project.max_recorded_demand_kw != null && project.max_recorded_demand_kw > 0;
  const hasAmiData = amiData.length > 0;

  if (method === 'measured' && !hasPeakDemand && !hasAmiData) {
    blockers.push({
      id: 'ami_data_required',
      severity: 'BLOCKER',
      title: 'No measured demand data',
      message:
        `NEC ${cite} requires measured demand data. Upload an AMI interval dataset or enter a peak demand value manually.`,
      fieldPath: 'ami_data',
      section: 'Existing Load',
      fixAction: nav('ami_data'),
    });
  } else if (method === 'measured' && hasAmiData) {
    const rangeDays = computeDateRangeDays(amiData);

    if (rangeDays !== null && rangeDays < 30) {
      blockers.push({
        id: 'ami_insufficient_range',
        severity: 'BLOCKER',
        title: `AMI data spans only ${Math.round(rangeDays)} day(s) — 30-day minimum required`,
        message:
          `NEC ${cite} is based on 1 year of maximum demand data, or, under its Exception, a continuous recording of at least 30 days. The uploaded dataset covers fewer than 30 days.`,
        fieldPath: 'ami_data',
        section: 'Existing Load',
        fixAction: nav('ami_data'),
      });
    }

    // Low record count warning (720 = 30 days × 24 hr intervals — the minimum reasonable dataset)
    if (amiData.length < 720) {
      warnings.push({
        id: 'ami_low_point_count',
        severity: 'WARNING',
        title: `Low AMI record count (${amiData.length} points)`,
        message:
          'Fewer than 720 data points were found. A 30-day dataset at hourly intervals should contain at least 720 records. Verify the import is complete.',
        fieldPath: 'ami_data',
        section: 'Existing Load',
        fixAction: nav('ami_data'),
      });
    }
  }

  // Equipment rating completeness ───────────────────────────────────────────

  // Proposed loads are optional — a project may simply ask "does this panel have
  // room". But once a row is filled in at all it has to be complete, because an
  // incomplete row still prints on the report as a proposed load.
  //
  // A row that is entirely untouched is not an incomplete entry: the New Loads
  // tab adds rows one click at a time, and an empty one carries no load and
  // never reaches the report. Blocking on it turned "no new loads" into an
  // unsatisfiable requirement. Dwelling-inventory rows are validated by the
  // traditional block above, not here.
  //
  // Removed rows (equipment taken out with this work) obey the same rules:
  // they print on every report and, under 2026, their rating and season
  // decide a credit against the measured demand.
  const entered = [...proposed, ...removedLoads(equipment)].filter(eq => !isBlankProposedRow(eq));
  if (entered.length > 0) {
    const name = (eq: Equipment) => eq.equipment_name?.trim() || `Item #${eq.sort_order}`;

    const unrated = entered.filter(eq => !eq.amps && !eq.kw);
    if (unrated.length > 0) {
      blockers.push({
        id: 'equipment_rating_required',
        severity: 'BLOCKER',
        title: `${unrated.length} equipment item(s) missing a rating`,
        message: `The following new or removed loads have no ampere or kW rating and cannot be included in the load calculation: ${unrated.map(name).join(', ')}. Add a rating or remove the item.`,
        fieldPath: 'equipment',
        section: 'New Loads',
        fixAction: nav('equipment'),
      });
    }

    // Without a type there is no basis for deciding whether the load runs during
    // the peak, so it was silently excluded from the adequacy result — making the
    // verdict more favourable than the evidence supports.
    const untyped = entered.filter(eq => !eq.equipment_type);
    if (untyped.length > 0) {
      blockers.push({
        id: 'equipment_type_required',
        severity: 'BLOCKER',
        title: `${untyped.length} equipment item(s) missing a type`,
        message: `The following new or removed loads have no equipment type: ${untyped.map(name).join(', ')}. The type determines whether a load is counted against the peak, so the calculation cannot be completed without it.`,
        fieldPath: 'equipment',
        section: 'New Loads',
        fixAction: nav('equipment'),
      });
    }

    // Source is an evidence claim — nameplate reading vs. minimum circuit
    // ampacity. The report used to print "Nameplate" whenever nothing was chosen,
    // asserting on the preparer's behalf something they never said.
    const sourceless = entered.filter(eq => !eq.source);
    if (sourceless.length > 0) {
      blockers.push({
        id: 'equipment_source_required',
        severity: 'BLOCKER',
        title: `${sourceless.length} equipment item(s) missing a rating source`,
        message: `The following new or removed loads do not state where their rating came from: ${sourceless.map(name).join(', ')}. Select Nameplate or MCA — the report records this as the basis for the rating.`,
        fieldPath: 'equipment',
        section: 'New Loads',
        fixAction: nav('equipment'),
      });
    }

    // 2026: a new heat pump must say whether its rating includes backup strip
    // heat. Table 120.83 counts central resistance heat at 80% and other new
    // loads at 50%; the engine counts an unanswered row at 80%, but a filed
    // report should rest on the preparer's answer, not on that default.
    if (cites.edition === '2026') {
      const unanswered = proposed.filter(
        eq => !isBlankProposedRow(eq) && eq.equipment_type === 'Heat Pump' && eq.includes_backup_heat == null
      );
      if (unanswered.length > 0) {
        blockers.push({
          id: 'heat_pump_backup_heat_required',
          severity: 'BLOCKER',
          title: `${unanswered.length} heat pump(s) need the backup strip heat question answered`,
          message: `Say whether the rating entered for ${unanswered.map(name).join(', ')} includes backup strip heat. Under NEC 2026 Table 120.83, central electric resistance heat counts at 80% and other new loads at 50%.`,
          fieldPath: 'equipment',
          section: 'New Loads',
          fixAction: nav('equipment'),
        });
      }
    }

    // An override without the preparer's own words leaves the report asserting
    // one thing in the Counted column and the opposite in the Explanation column,
    // both in TrueLoad's voice.
    // Concurrency is a measured-method concept; a traditional basis never
    // demands an override explanation.
    const peakSeason = method === 'measured' ? computePeakSeason(project, amiData) : 'Unknown';
    if (peakSeason !== 'Unknown') {
      const unexplained = entered.filter(
        eq =>
          eq.equipment_type &&
          isConcurrencyOverridden(eq, peakSeason) &&
          !hasOwnConcurrencyReason(eq, peakSeason)
      );
      if (unexplained.length > 0) {
        blockers.push({
          id: 'equipment_override_reason_required',
          severity: 'BLOCKER',
          title: `${unexplained.length} concurrency override(s) need an explanation`,
          message: `The concurrent-at-peak setting was changed from TrueLoad's determination for: ${unexplained.map(name).join(', ')}. Replace the pre-written explanation with your own reasoning — otherwise the report states one conclusion and gives the opposite justification.`,
          fieldPath: 'equipment',
          section: 'New Loads',
          fixAction: nav('equipment'),
        });
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TIER 2 — WARNINGS (soft blockers requiring acknowledgment)
  // ─────────────────────────────────────────────────────────────────────────

  // AHJ jurisdiction missing
  if (!project.ahj_jurisdiction?.trim()) {
    warnings.push({
      id: 'ahj_missing',
      severity: 'WARNING',
      title: 'Authority Having Jurisdiction (AHJ) missing',
      message: 'The AHJ jurisdiction is recommended for permit and code compliance documentation. You may proceed without it, but some jurisdictions may require this information.',
      fieldPath: 'ahj_jurisdiction',
      section: 'Project Info',
      fixAction: nav('ahj_jurisdiction'),
    });
  }

  // License number missing
  if (!project.preparer_license?.trim()) {
    warnings.push({
      id: 'preparer_license_missing',
      severity: 'WARNING',
      title: 'License Number missing',
      message: `NEC ${basisCite} evaluations are typically performed by licensed electricians or engineers. If you do not have a license number, you may proceed, but be aware that some jurisdictions may require professional certification for load calculations submitted with permit applications.`,
      fieldPath: 'preparer_license',
      section: 'Project Info',
      fixAction: nav('preparer_license'),
    });
  }

  // AMI data quality notes
  const qualityNotes = project.ami_data_quality_notes?.trim() ?? '';
  if (method === 'measured' && qualityNotes) {
    const hasConversion = /kWh|convert|unit/i.test(qualityNotes);
    const hasGaps = /gap|missing|irregular|inconsistent/i.test(qualityNotes);
    const hasDST = /DST|daylight|duplicate|clock/i.test(qualityNotes);

    if (hasConversion) {
      warnings.push({
        id: 'ami_unit_conversion',
        severity: 'WARNING',
        title: 'Unit conversion applied to AMI data',
        message:
          'kWh-to-kW or unit conversion was performed during ingestion. Verify the interval duration assumption and ensure the conversion is disclosed in the report.',
        fieldPath: 'ami_data_quality_notes',
        section: 'Existing Load',
        fixAction: { type: 'NONE' },
      });
    }

    if (hasGaps) {
      warnings.push({
        id: 'ami_data_gaps',
        severity: 'WARNING',
        title: 'Data gaps or irregular intervals detected',
        message:
          `The AMI dataset contains gaps, missing records, or irregular intervals. Per NEC ${cite} defensibility principles, this must be disclosed. Confirm coverage is sufficient to represent peak demand conditions.`,
        fieldPath: 'ami_data_quality_notes',
        section: 'Existing Load',
        fixAction: { type: 'NONE' },
      });
    }

    if (hasDST) {
      warnings.push({
        id: 'ami_dst_artifacts',
        severity: 'WARNING',
        title: 'DST or clock-change artifacts in AMI data',
        message:
          'Daylight saving time artifacts (duplicate or missing timestamps) were detected during import. These have been resolved but should be noted in the report.',
        fieldPath: 'ami_data_quality_notes',
        section: 'Existing Load',
        fixAction: { type: 'NONE' },
      });
    }

    if (!hasConversion && !hasGaps && !hasDST) {
      warnings.push({
        id: 'ami_quality_notes_present',
        severity: 'WARNING',
        title: 'AMI data quality notes present',
        message: `Data quality issues were recorded during ingestion: "${qualityNotes.slice(0, 120)}${qualityNotes.length > 120 ? '…' : ''}". These should be reviewed and disclosed in the report.`,
        fieldPath: 'ami_data_quality_notes',
        section: 'Existing Load',
        fixAction: { type: 'NONE' },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TIER 3 — ADVISORIES (non-blocking suggestions)
  // ─────────────────────────────────────────────────────────────────────────

  // Empty rows are dropped rather than blocked, so say so — a preparer who
  // meant to fill one in should not find it silently missing from the report.
  const blankRows =
    proposed.length + removedLoads(equipment).length - entered.length;
  if (blankRows > 0) {
    advisories.push({
      id: 'equipment_blank_rows',
      severity: 'ADVISORY',
      title: `${blankRows} empty equipment row(s) will be left off the report`,
      message:
        'Rows with no name, type, or rating carry no load and are omitted from the calculation and the report. Fill them in or delete them if that is not what you intended.',
      fieldPath: 'equipment',
      section: 'New Loads',
      fixAction: nav('equipment'),
    });
  }

  if (!project.assumptions_notes?.trim()) {
    advisories.push({
      id: 'assumptions_notes_empty',
      severity: 'ADVISORY',
      title: 'Assumptions & Notes section is empty',
      message:
        'Report templates include an "Assumptions and Notes" section. Consider documenting key assumptions (occupancy type, demand factor basis, measurement period representativeness) before completing the project.',
      fieldPath: 'assumptions_notes',
      section: 'Project Info',
      fixAction: nav('assumptions_notes'),
    });
  }

  if (method === 'measured') {
    advisories.push({
      id: 'measurement_period_representative',
      severity: 'ADVISORY',
      title: 'Confirm measurement period is representative',
      message:
        `NEC ${cite} relies on measured peak demand to establish adequacy. Confirm the measurement period covers seasonal peak conditions (e.g., summer cooling or winter heating) for this site.`,
      section: 'Existing Load',
      fixAction: { type: 'NONE' },
    });
  }

  return {
    blockers,
    warnings,
    advisories,
    summary: {
      blockerCount: blockers.length,
      warningCount: warnings.length,
      advisoryCount: advisories.length,
    },
  };
}
